import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { gtmNewsletterSubmit } from '../utils/gtm';
import {
  Container,
  Box,
  Grid,
  Typography,
  Button,
  Chip,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckIcon,
  TrendingUp as TrendingUpIcon,
  Verified as VerifiedIcon,
  Update as UpdateIcon,
  EmailOutlined,
} from '@mui/icons-material';
import SEOHead from '../components/SEO/SEOHead';
import BreadcrumbNav from '../components/SEO/BreadcrumbNav';
import TrustStrip from '../components/Layout/TrustStrip';
import { useLocalizedPath } from '../hooks/useLocalizedPath';

/* ── Schema ── */
const schema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Comparadores de productos financieros - FinMaple',
  description:
    'Compara tarjetas de crédito, cuentas bancarias, hipotecas, remesas, seguros, GICs y cuentas registradas en Canadá. Herramientas gratuitas para recién llegados latinos.',
  provider: { '@type': 'Organization', name: 'FinMaple', url: 'https://www.finmaple.com' },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://www.finmaple.com/' },
      { '@type': 'ListItem', position: 2, name: 'Comparadores', item: 'https://www.finmaple.com/comparadores' },
    ],
  },
};

/* ── Goal shortcuts (Section 1 — horizontal pills that also set filters) ── */
const GOALS = [
  { id: 'credito', label: 'Construir crédito', filter: 'no_credit', image: '/images/icons/icon-credit-card.webp' },
  { id: 'cuenta', label: 'Abrir cuenta bancaria', filter: 'newcomer', image: '/images/icons/icon-bank.webp' },
  { id: 'casa', label: 'Comprar casa', filter: 'save_interest', image: '/images/icons/icon-house.webp' },
  { id: 'remesas', label: 'Enviar dinero', filter: 'bonus', image: '/images/icons/icon-remittance.webp' },
];

/* ── Smart filter options ── */
const FILTERS = [
  { value: 'all', label: 'Todos' },
  { value: 'newcomer', label: 'Recién llegado (<2 años)' },
  { value: 'established', label: 'Ya establecido' },
  { value: 'no_credit', label: 'Sin historial crediticio' },
  { value: 'save_interest', label: 'Busco ahorrar intereses' },
  { value: 'bonus', label: 'Busco bono / cashback' },
];

/* ── Comparator data ── */
const comparators = [
  {
    id: 'card-tarjetas',
    title: 'Tarjetas de crédito',
    description: 'Encuentra la tarjeta ideal para construir crédito en Canadá como newcomer.',
    routeKey: 'creditCards',
    iconSrc: '/images/icons/credit-card.svg',
    chips: ['110+ tarjetas', 'Newcomer-friendly'],
    cta: 'Comparar ahora',
    popularity: 'Más usado por newcomers',
    badge: 'Incluye FinMaple Score',
    badgeIcon: 'score',
    featured: true,
    relevance: ['newcomer', 'no_credit', 'bonus'],
  },
  {
    id: 'card-cuentas',
    title: 'Cuentas bancarias',
    description: 'Compara cuentas chequing y savings con programas para recién llegados.',
    routeKey: 'accountComparator',
    iconSrc: '/images/icons/bank.svg',
    chips: ['12 cuentas', 'Bonos hasta $800'],
    cta: 'Explorar opciones',
    popularity: 'Más consultado este mes',
    badge: 'Actualizado marzo 2026',
    badgeIcon: 'updated',
    featured: true,
    relevance: ['newcomer', 'no_credit', 'bonus', 'established'],
  },
  {
    id: 'card-hipotecas',
    title: 'Hipotecas',
    description: 'Programas hipotecarios que aceptan newcomers - incluso sin historial crediticio.',
    routeKey: 'mortgages',
    iconSrc: '/images/icons/house.svg',
    chips: ['7 programas', 'Newcomers'],
    cta: 'Calcular tasas',
    popularity: null,
    badge: 'Incluye FinMaple Score',
    badgeIcon: 'score',
    featured: false,
    relevance: ['newcomer', 'no_credit', 'save_interest', 'established'],
  },
  {
    id: 'card-remesas',
    title: 'Remesas',
    description: 'Envía dinero a Latinoamérica con las mejores tasas y menor comisión.',
    routeKey: 'remittanceComparator',
    iconSrc: '/images/icons/globe.svg',
    chips: ['5 proveedores', 'Mid-market rates'],
    cta: 'Comparar ahora',
    popularity: null,
    badge: 'Comparación en tiempo real',
    badgeIcon: 'realtime',
    featured: false,
    relevance: ['newcomer', 'established', 'save_interest', 'bonus'],
  },
  {
    id: 'card-seguros',
    title: 'Seguros',
    description: 'Seguros esenciales para newcomers: salud, auto, hogar y vida.',
    routeKey: 'insuranceComparator',
    iconSrc: '/images/icons/shield.svg',
    chips: ['5 tipos', 'Obligatorios + opcionales'],
    cta: 'Explorar opciones',
    popularity: null,
    badge: 'Actualizado marzo 2026',
    badgeIcon: 'updated',
    featured: false,
    relevance: ['newcomer', 'established'],
  },
  {
    id: 'card-cuentas-reg',
    title: 'Cuentas registradas',
    description: 'TFSA, RRSP, FHSA y RESP - entiende cada cuenta y sus beneficios fiscales.',
    routeKey: 'registeredAccounts',
    iconSrc: '/images/icons/piggy-bank.svg',
    chips: ['4 cuentas', 'Educativo'],
    cta: 'Explorar opciones',
    popularity: null,
    badge: 'Incluye FinMaple Score',
    badgeIcon: 'score',
    featured: false,
    relevance: ['established', 'save_interest'],
  },
  {
    id: 'card-gic',
    title: 'Tasas GIC',
    description: 'Compara tasas de GIC garantizadas por término - sin riesgo.',
    routeKey: 'gicRates',
    iconSrc: '/images/icons/bar-chart.svg',
    chips: ['Tasas 2026', 'CDIC protegido'],
    cta: 'Comparar ahora',
    popularity: null,
    badge: 'Actualizado marzo 2026',
    badgeIcon: 'updated',
    featured: false,
    relevance: ['established', 'save_interest'],
  },
];

const NAVY = '#1E3A5F';

/* ── Badge icon helper ── */
const BadgeIconMap = {
  score: VerifiedIcon,
  updated: UpdateIcon,
  realtime: TrendingUpIcon,
};

/* ── Fade-in on scroll hook ── */
const useFadeInOnScroll = () => {
  const ref = useRef(null);

  useEffect(() => {
    if (navigator.webdriver) return;
    const el = ref.current;
    if (!el) return;
    if (el.getBoundingClientRect().top < window.innerHeight) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(20px)';
    el.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out';
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return ref;
};

/* ── "How it works" steps ── */
const howItWorksSteps = [
  {
    icon: '📊',
    title: 'Analizamos datos oficiales',
    desc: 'Recopilamos tasas, fees y condiciones directamente de instituciones financieras canadienses.',
  },
  {
    icon: '🧮',
    title: 'Calculamos el FinMaple Score',
    desc: 'Nuestro algoritmo evalúa cada producto según relevancia para recién llegados.',
  },
  {
    icon: '🔄',
    title: 'Actualizamos mensualmente',
    desc: 'Cada comparador se revisa y actualiza para reflejar las condiciones vigentes del mercado.',
  },
];

/* ══════════════════════════════════════════════
   PAGE COMPONENT
   ══════════════════════════════════════════════ */
const ComparadoresHubPage = () => {
  const { i18n } = useTranslation();
  const lp = useLocalizedPath();
  const [activeFilter, setActiveFilter] = useState('all');
  const gridRef = useRef(null);

  const handleGoalClick = (filter) => {
    setActiveFilter(filter);
    if (window.plausible) window.plausible('Comparator Goal Click', { props: { goal: filter } });
    gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleFilterChange = (_, value) => {
    if (value) {
      setActiveFilter(value);
      if (window.plausible) window.plausible('Comparator Filter', { props: { filter: value } });
    }
  };

  const isRelevant = (item) =>
    activeFilter === 'all' || item.relevance.includes(activeFilter);

  /* Sort: relevant first, then featured first within each group */
  const sortedComparators = [...comparators].sort((a, b) => {
    const aRel = isRelevant(a) ? 0 : 1;
    const bRel = isRelevant(b) ? 0 : 1;
    if (aRel !== bRel) return aRel - bRel;
    const aFeat = a.featured ? 0 : 1;
    const bFeat = b.featured ? 0 : 1;
    return aFeat - bFeat;
  });

  const howRef = useFadeInOnScroll();
  const retentionRef = useFadeInOnScroll();

  return (
    <Box sx={{ bgcolor: '#FFFFFF', minHeight: '100vh' }}>
      <SEOHead
        seoKey="comparators"
        title="Comparadores de productos financieros en Canadá | FinMaple"
        description="Compara tarjetas de crédito, cuentas bancarias, hipotecas, remesas y seguros en Canadá. Herramientas gratuitas diseñadas para recién llegados latinos."
        canonical="/comparadores"
        routeKey="comparators"
        schema={schema}
        noIndex={i18n.language !== 'es'}
      />

      {/* ═══════════════════════════════════════════════════
          1. HERO — title + subtitle (absorbs AtomicAnswer)
          ═══════════════════════════════════════════════════ */}
      <Box
        sx={{
          background: 'linear-gradient(180deg, #F7F9FC 0%, #FFFFFF 100%)',
          borderBottom: '1px solid #EEF0F3',
          pt: { xs: 3, md: 4 },
          pb: { xs: 3, md: 4 },
        }}
      >
        <Container maxWidth="lg">
          <BreadcrumbNav items={[{ label: 'Comparadores' }]} />

          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700, color: '#0F1E3A', mt: 1.5, mb: 1,
              lineHeight: 1.15, fontSize: { xs: '24px', md: '38px' },
              letterSpacing: '-0.02em',
            }}
          >
            Comparadores
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '14px', md: '16px' }, color: '#5F6B7A',
              lineHeight: 1.6, maxWidth: '60ch', mb: { xs: 2.5, md: 3 },
            }}
          >
            Herramientas gratuitas para comparar productos financieros canadienses.
            Datos verificados, enlaces directos y FinMaple Score en la mayoría de categorías.
          </Typography>

          {/* ── Goal shortcuts (horizontal pills, act as filter+scroll) ── */}
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', mb: 1.5 }}>
            ¿Qué estás buscando hoy?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 0 }}>
            {GOALS.map((goal) => (
              <Box
                key={goal.id}
                onClick={() => handleGoalClick(goal.filter)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleGoalClick(goal.filter); }
                }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: { xs: 1.5, sm: 2 }, py: { xs: 0.75, sm: 1 },
                  bgcolor: activeFilter === goal.filter ? '#1E3A5F' : '#FFFFFF',
                  color: activeFilter === goal.filter ? '#FFFFFF' : '#1A1A1A',
                  border: '1px solid',
                  borderColor: activeFilter === goal.filter ? '#1E3A5F' : '#E5E7EB',
                  borderRadius: '10px', cursor: 'pointer',
                  transition: 'all 0.18s ease',
                  boxShadow: activeFilter === goal.filter
                    ? '0 4px 12px rgba(30,58,95,0.2)'
                    : '0 1px 3px rgba(0,0,0,0.04)',
                  '&:hover': {
                    borderColor: '#1E3A5F',
                    boxShadow: '0 4px 12px rgba(30,58,95,0.12)',
                    transform: 'translateY(-1px)',
                  },
                  '&:focus-visible': { outline: '2px solid #1E3A5F', outlineOffset: 2 },
                }}
              >
                <Box
                  component="img"
                  src={goal.image}
                  alt=""
                  loading="lazy"
                  sx={{
                    width: 28, height: 28, objectFit: 'contain',
                    filter: activeFilter === goal.filter ? 'brightness(0) invert(1)' : 'none',
                    transition: 'filter 0.18s ease',
                  }}
                />
                <Typography sx={{ fontSize: '13px', fontWeight: 600, lineHeight: 1.2 }}>
                  {goal.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════
          2. FILTERS (inline, connected to grid)
          ═══════════════════════════════════════════════════ */}
      <Box sx={{ bgcolor: '#FFFFFF', borderBottom: '1px solid #EEF0F3', py: { xs: 1.5, md: 2 } }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#5F6B7A', whiteSpace: 'nowrap' }}>
              Filtrar por situación:
            </Typography>
            <ToggleButtonGroup
              value={activeFilter}
              exclusive
              onChange={handleFilterChange}
              sx={{
                flexWrap: 'wrap',
                gap: 0.5,
                '& .MuiToggleButtonGroup-grouped': {
                  border: '1px solid #E5E7EB !important',
                  borderRadius: '8px !important',
                  ml: '0 !important',
                },
              }}
              aria-label="Filtrar comparadores por situación"
            >
              {FILTERS.map((f) => (
                <ToggleButton
                  key={f.value}
                  value={f.value}
                  sx={{
                    textTransform: 'none', fontSize: '12px', fontWeight: 500,
                    px: 1.5, py: 0.5, color: '#5F6B7A',
                    transition: 'all 0.15s ease',
                    '&.Mui-selected': {
                      bgcolor: '#1E3A5F', color: '#fff',
                      borderColor: '#1E3A5F !important',
                      '&:hover': { bgcolor: '#1A3152' },
                    },
                    '&:hover': { bgcolor: '#F7F9FC' },
                  }}
                >
                  {f.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════
          3. COMPARATOR GRID — featured (2) + standard (5)
          ═══════════════════════════════════════════════════ */}
      <Box ref={gridRef} sx={{ scrollMarginTop: '80px' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 5 } }}>
          <Grid container spacing={3}>
            {sortedComparators.map((item) => {
              const highlighted = isRelevant(item);
              const BadgeIcon = BadgeIconMap[item.badgeIcon] || VerifiedIcon;
              const isFeatured = item.featured && highlighted;

              return (
                <Grid
                  item
                  xs={12}
                  sm={isFeatured ? 6 : 6}
                  md={isFeatured ? 6 : 4}
                  key={item.id}
                  id={item.id}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 3, md: isFeatured ? 4.5 : 3 },
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: isFeatured ? '18px' : '14px',
                      border: isFeatured ? '1.5px solid' : '1px solid',
                      borderColor: highlighted
                        ? (isFeatured ? '#D0D5DD' : '#E5E7EB')
                        : '#F3F4F6',
                      bgcolor: '#fff',
                      boxShadow: highlighted
                        ? (isFeatured
                            ? '0 8px 28px rgba(0,0,0,0.07)'
                            : '0 2px 10px rgba(0,0,0,0.03)')
                        : '0 1px 4px rgba(0,0,0,0.02)',
                      opacity: highlighted ? 1 : 0.4,
                      transform: highlighted ? 'none' : 'scale(0.98)',
                      transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease, opacity 0.35s ease',
                      '&:hover': highlighted
                        ? {
                            transform: isFeatured ? 'translateY(-5px)' : 'translateY(-3px)',
                            boxShadow: isFeatured
                              ? '0 18px 44px rgba(0,0,0,0.09)'
                              : '0 10px 28px rgba(0,0,0,0.06)',
                            borderColor: '#D0D5DD',
                          }
                        : {},
                    }}
                  >
                    {/* Popularity badge */}
                    {item.popularity && highlighted && (
                      <Chip
                        label={item.popularity}
                        size="small"
                        sx={{
                          alignSelf: 'flex-start', mb: 1.5, height: 24,
                          fontSize: '12px', fontWeight: 600,
                          bgcolor: 'rgba(249,168,37,0.10)', color: '#C68A00',
                          border: '1px solid rgba(249,168,37,0.3)',
                        }}
                      />
                    )}

                    {/* Icon tile */}
                    <Box
                      sx={{
                        width: isFeatured ? 64 : 56, height: isFeatured ? 64 : 56,
                        borderRadius: '14px', bgcolor: '#F7F9FC',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mb: 2.5,
                      }}
                    >
                      <Box
                        component="img"
                        src={item.iconSrc}
                        alt={item.title}
                        sx={{
                          width: isFeatured ? 40 : 34, height: isFeatured ? 40 : 34,
                          objectFit: 'contain',
                          filter: 'brightness(0) saturate(100%) invert(11%) sepia(46%) saturate(4700%) hue-rotate(229deg) brightness(83%) contrast(102%)',
                        }}
                      />
                    </Box>

                    {/* Title */}
                    <Typography
                      variant="body1"
                      component="h2"
                      sx={{
                        fontWeight: 700, color: NAVY, mb: 0.75, lineHeight: 1.3,
                        fontSize: isFeatured ? '18px' : '16px',
                      }}
                    >
                      {item.title}
                    </Typography>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 2, flex: 1 }}
                    >
                      {item.description}
                    </Typography>

                    {/* Chips row */}
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                      {item.chips.map((chip) => (
                        <Chip
                          key={chip}
                          label={chip}
                          size="small"
                          sx={{
                            fontSize: '12px', fontWeight: 500, height: 24,
                            bgcolor: '#F7F9FC', color: '#5F6B7A', border: 'none',
                          }}
                        />
                      ))}
                    </Box>

                    {/* Utility badge */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2.5 }}>
                      <BadgeIcon sx={{ fontSize: 14, color: '#2E7D32' }} />
                      <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#2E7D32' }}>
                        {item.badge}
                      </Typography>
                    </Box>

                    {/* CTA */}
                    <Button
                      component={RouterLink}
                      to={lp(item.routeKey)}
                      variant={isFeatured ? 'contained' : 'outlined'}
                      endIcon={<ArrowForwardIcon sx={{ fontSize: '16px' }} />}
                      sx={{
                        alignSelf: 'flex-start',
                        textTransform: 'none', fontWeight: 600,
                        borderRadius: isFeatured ? '10px' : '8px',
                        fontSize: isFeatured ? '15px' : '13px',
                        px: isFeatured ? 3 : 2.5,
                        py: isFeatured ? 1.3 : 0.9,
                        transition: 'transform 0.18s ease, box-shadow 0.18s ease, background-color 0.18s ease',
                        ...(isFeatured
                          ? {
                              bgcolor: '#1E3A5F', color: '#fff',
                              '&:hover': {
                                bgcolor: '#1A3152',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 6px 16px rgba(30,58,95,0.20)',
                              },
                            }
                          : {
                              color: '#1E3A5F', borderColor: '#D0D5DD',
                              '&:hover': {
                                bgcolor: '#F7F9FC', borderColor: '#1E3A5F',
                                transform: 'translateY(-1px)',
                              },
                            }),
                      }}
                    >
                      {item.cta}
                    </Button>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════
          4. HOW COMPARATORS WORK
          ═══════════════════════════════════════════════════ */}
      <Box ref={howRef} component="section" sx={{ bgcolor: '#F7F9FC', py: { xs: 5, md: 6 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            component="h2"
            sx={{ fontWeight: 600, color: '#1A1A1A', mb: 1, textAlign: 'center' }}
          >
            Cómo funcionan nuestros comparadores
          </Typography>
          <Typography
            sx={{
              color: '#5F6B7A', fontSize: '15px', lineHeight: 1.6,
              mb: 5, textAlign: 'center', maxWidth: '55ch', mx: 'auto',
            }}
          >
            Cada herramienta sigue un proceso riguroso para ofrecerte datos confiables.
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {howItWorksSteps.map((step, i) => (
              <Grid item xs={12} sm={4} key={i}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '36px', mb: 1.5 }}>{step.icon}</Typography>
                  <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#1A1A1A', mb: 0.75 }}>
                    {step.title}
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: '#5F6B7A', lineHeight: 1.6, maxWidth: '30ch', mx: 'auto' }}>
                    {step.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 4 }}>
            <CheckIcon sx={{ fontSize: 16, color: '#2E7D32' }} />
            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#2E7D32' }}>
              Transparente · Sin costo para usuarios
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════
          5. RETENTION BLOCK
          ═══════════════════════════════════════════════════ */}
      <Box ref={retentionRef} component="section" sx={{ bgcolor: '#1A1A1A', py: { xs: 5, md: 6 } }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <Typography
            sx={{
              fontSize: '12px', fontWeight: 600, color: 'rgba(255,255,255,0.5)',
              textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1.5,
            }}
          >
            ¿Primera vez en Canadá?
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#FFFFFF', mb: 1 }}>
            ¿No sabes por dónde empezar?
          </Typography>
          <Typography
            sx={{
              fontSize: '15px', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6,
              mb: 3.5, maxWidth: '45ch', mx: 'auto',
            }}
          >
            Nuestra guía paso a paso te explica todo: desde abrir tu primera cuenta hasta construir crédito y comprar casa.
          </Typography>
          <Button
            component={RouterLink}
            to={lp('getStarted')}
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            sx={{
              bgcolor: '#FFFFFF', color: '#1A1A1A', textTransform: 'none',
              fontWeight: 700, fontSize: '15px', px: 4, py: 1.5, borderRadius: '12px',
              transition: 'transform 0.18s ease, box-shadow 0.18s ease',
              '&:hover': {
                bgcolor: '#F7F9FC',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
              },
            }}
          >
            Guía para recién llegados
          </Button>
        </Container>
      </Box>

      {/* ── Compact newsletter (secondary to retention CTA) ── */}
      <Box component="section" sx={{ bgcolor: '#F7F9FC', borderTop: '1px solid #EEF0F3', py: { xs: 2.5, md: 3 } }}>
        <Container maxWidth="lg">
          <Box
            component="form"
            action="https://buttondown.com/api/emails/embed-subscribe/Finmaple"
            method="post"
            onSubmit={() => {
              if (window.plausible) window.plausible('Newsletter Submit', { props: { source: 'comparadores-compact' } });
              gtmNewsletterSubmit({ source: 'comparadores-compact' });
            }}
            sx={{
              display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center', gap: { xs: 1.5, sm: 2 },
              maxWidth: 580, mx: 'auto',
            }}
          >
            <EmailOutlined sx={{ fontSize: 18, color: '#1E3A5F', display: { xs: 'none', sm: 'block' } }} />
            <Typography sx={{ fontSize: '13px', color: '#5F6B7A', fontWeight: 500 }}>
              Recibe comparaciones y guías financieras cada semana.
            </Typography>
            <TextField
              name="email"
              type="email"
              required
              size="small"
              placeholder="tu@email.com"
              sx={{
                flex: 1, minWidth: 160,
                '& .MuiOutlinedInput-root': {
                  height: 36, borderRadius: '8px', fontSize: '13px',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#1E3A5F' },
                  '&.Mui-focused fieldset': { borderColor: '#1E3A5F' },
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              size="small"
              sx={{
                height: 36, px: 2.5, fontSize: '13px', fontWeight: 700,
                borderRadius: '8px', bgcolor: '#1E3A5F', color: '#fff',
                textTransform: 'none', whiteSpace: 'nowrap',
                '&:hover': { bgcolor: '#1A3152' },
              }}
            >
              Suscribirme
            </Button>
          </Box>
        </Container>
      </Box>

      <TrustStrip />
    </Box>
  );
};

export default ComparadoresHubPage;
